import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import {
  Box, Typography, ListItemIcon, Tooltip, IconButton, Chip, CircularProgress,
  Dialog, DialogContent, DialogActions, TextField, Button, Snackbar, Alert
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CommonButton from "../CommonButton/CommonButton";
import DoneIcon from "@mui/icons-material/Done";
import LinearProgress from "@mui/material/LinearProgress";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import { useAuth } from "../../auth/AuthProvider";
import { useHistory } from "../../../context/HistoryContext";
import { fetchActivityRecords, fetchActivityReportRequestById, isPartnerActivityReportReadOnly, mapActivityRequestStatusCodeToLabel } from "../../../services/activityReportService";

/* =======================
    HELPER — Editable column header with pencil icon (matches PortfolioTable)
   ======================= */
const EditableHeader = ({ label, tooltip }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <span>{label}</span>
    <EditIcon sx={{ fontSize: 12, color: "#1976d2", ml: 0.5 }} />
    {tooltip && (
      <Tooltip title={tooltip} arrow placement="top">
        <IconButton size="small" sx={{ p: 0.25 }}>
          <HelpOutlineIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const PartnerReports = forwardRef(
  (
    {
      searchText,
      tableRef,
      isCheckboxColumn,
      isStatusColumn,
      statusType,
      requestId,
      reportName
    },
    ref,
  ) => {
    const apiRef = useGridApiRef();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, role } = useAuth();
    const { openHistory } = useHistory();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [completeComment, setCompleteComment] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [tourStep, setTourStep] = useState(role === "partner" ? 1 : null); // 1, 2, 3, or null (done)
    const [customKpiColumns, setCustomKpiColumns] = useState([]);
    const [requestStatusCode, setRequestStatusCode] = useState(null);
    const tourKey = "partnerReports_tourDone";
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';
    const CUSTOM_KPI_LABEL_MAP = {
      top_line_kpi: "ARR",
      carr: "CARR",
      quarterly_revenue: "Quarterly Revenue",
      monthly_revenue: "Monthly Revenue",
      aum: "AUM",
      deployment_revenue: "Deployment Revenue",
      rev_run_rate: "Rev. Run Rate",
      quarterly_ebitda: "Quarterly EBITDA",
      monthly_gmv: "Monthly GMV",
      ytd_bookings: "YTD Bookings",
    };

    useEffect(() => {
      const done = localStorage.getItem(tourKey);
      if (done) setTourStep(null);
    }, []);

    const nextTourStep = () => {
      if (tourStep === 3) {
        setTourStep(null);
        localStorage.setItem(tourKey, "true");
      } else {
        setTourStep((s) => s + 1);
      }
    };


    // Fetch activity records when component mounts or requestId changes
    useEffect(() => {
      const loadActivityRecords = async () => {
        try {
          setLoading(true);
          setError(null);

          const partnerName = "Rob M. Stavis";
          const partnerDocName = "RMS";

          // Await the fetchActivityRecords promise
          const activityRecords = await fetchActivityRecords(
            requestId,
            partnerName,
            partnerDocName,
          );

          console.log("Activity records received:", activityRecords);
          console.log("First record fields:", activityRecords.data?.[0]);


          // Update state with the fetched records
          setReports(activityRecords.data);
        } catch (error) {
          console.error("Failed to fetch activity records:", error);
          setError(error.message || "Failed to load activity records");
          // You might want to show a snackbar or toast notification here
        } finally {
          setLoading(false);
        }
      };

      if (requestId) {
        loadActivityRecords();
      } else {
        setLoading(false);
      }
    }, [requestId]); // Re-fetch if requestId changes

    useEffect(() => {
      const loadRequestData = async () => {
        if (!requestId) return;
        try {
          const data = await fetchActivityReportRequestById(requestId);
          const item = data?.data ?? data;
          const customkpis = item?.request_data?.customkpis || [];
          setCustomKpiColumns(customkpis);
          if (typeof item?.status === "number") {
            setRequestStatusCode(item.status);
          }
        } catch (err) {
          console.error('Failed to fetch request data:', err);
        }
      };
      loadRequestData();
    }, [requestId]);

    const resolvedStatusType =
      statusType && statusType !== "Unknown Status"
        ? statusType
        : typeof requestStatusCode === "number"
          ? mapActivityRequestStatusCodeToLabel(requestStatusCode, role)
          : statusType;

    const partnerReadOnly = isPartnerActivityReportReadOnly(
      role,
      requestStatusCode,
      resolvedStatusType,
    );

    const gradeOptions = [
      "A+",
      "A",
      "A-",
      "B+",
      "B",
      "B-",
      "C+",
      "C",
      "C-",
      "D",
      "F",
    ];

    const statusColors = {
      Pending: "#F7E3BA",
      "Pending Completion": "#ECDFF2",
      Complete: "#E7EBC7",
      "60% Progress": "#F7E3BA",
    };

    const handleProcessRowUpdate = async (newRow, oldRow) => {
      const customKpiFields = {};
      customKpiColumns.forEach(key => {
        customKpiFields[key] = newRow[key] ?? null;
      });
      try {
        const res = await fetch(`${API_BASE_URL}/activity-reports/records`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: newRow.id,
            request_id: requestId,
            grade: newRow.grade ?? null,
            revenue: newRow.revenue ?? null,
            cash_balance: newRow.cash_balance ?? null,
            runway: newRow.runway ?? null,
            net_income: newRow.net_income ?? null,
            total_head_count: newRow.total_head_count ?? null,  // ADD
            top_line_kpi: newRow.top_line_kpi ?? null,          // ADD (ARR)
            news: newRow.news ?? null,
            invested_capital: newRow.invested_capital ?? null,
            cashout_date: newRow.cashout_date ?? null,
            ...customKpiFields,  // ← dynamic custom KPI fields
          }),
        });

        if (!res.ok) throw new Error('Failed to update record');

        setReports((prev) =>
          prev.map((row) => (row.id === newRow.id ? newRow : row))
        );
        setHasChanges(true);
        return newRow;
      } catch (err) {
        console.error('Failed to save row:', err);
        return oldRow; // revert cell to old value on failure
      }
    };

    const handleSubmitRequest = async () => {
      try {
        // ADD THIS:
        console.log("Submitting with:", {
          request_id: requestId,
          partner_name: "Rob M. Stavis",
          partner_doc_name: "RMS",
        });
        console.log("First report row:", reports[0]);
        const res = await fetch(`${API_BASE_URL}/activity-reports/submit-partner`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            request_id: requestId,
            partner_name: "Rob M. Stavis",      // hardcoded for now
            partner_doc_name: "RMS",            // hardcoded for now
          }),
        });

        if (!res.ok) throw new Error('Submit failed');

        setHasChanges(false);
        navigate('/activity-reports');
      } catch (err) {
        console.error('Submit failed:', err);
      }
    };

    const handleCompleteRequest = async () => {
      try {
        setCompleting(true);
        // await completeRequest(requestId); // uncomment when backend ready
        setIsCompleted(true);
        setCompleteDialogOpen(false);
        setCompleteComment('');
        // Show success snackbar — needs snackbar state or use existing one
      } catch (err) {
        console.error('Complete failed:', err);
      } finally {
        setCompleting(false);
      }
    };

    const showPartnerSubmit = !partnerReadOnly && (hasChanges || Boolean(location.state?.fromReviewSave));
    const flagButtonStyles = {
      minWidth: "40px",
      padding: "8px",
      marginRight: "8px",
    };

    const columns = useMemo(
      () => [
        // ── 1. Rank — NOT editable


        // ── 2. Company — NOT editable, pinned left
        {
          field: "company_name",
          headerName: "Company",
          width: 200,
          minWidth: 160,
          filterable: true,
          sortable: true,
          editable: false,
          renderCell: (params) => {
            console.log("ROW DATA:", params.row);   // ✅ ADD THIS LINE
            const isParent = params.row._isParent;
            const childCount = params.row._childCount || 0;
            const treeDepth = params.rowNode?.depth || 0;
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  pl: treeDepth * 4,
                  width: "100%",
                }}
              >
                {isParent ? (
                  <Chip
                    label={`${childCount} Companies`}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: "white",
                    }}
                  />
                ) : (
                  <Typography variant="body2">{params.value}</Typography>
                )}
              </Box>
            );
          },
        },
        {
          field: "rank",
          headerName: "Rank",
          width: 70,
          align: "right",
          headerAlign: "right",
          sortable: true,
          filterable: true,
          editable: role !== "operations" && !partnerReadOnly,
          type: "number",
          renderHeader: () => <EditableHeader label="Rank" />,
        },

        ...(isStatusColumn
          ? [
            // ── 3. Status — NOT editable
            {
              field: "Status",
              headerName: "Status",
              width: 160,
              filterable: true,
              sortable: true,
              editable: false,
              renderCell: (params) => {
                if (params.row._isParent) return null;
                return (
                  <Chip
                    label={params.value || "—"}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor:
                        statusColors[params.value] || "#f5f5f5",
                    }}
                  />
                );
              },
            },
            // ── 4. Last Activity — NOT editable
            {
              field: "LastActivity",
              headerName: "Last Activity",
              width: 140,
              filterable: true,
              sortable: true,
              editable: false,
              renderCell: (params) =>
                params.row._isParent ? (
                  <Chip
                    label={params.value}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: "white",
                      borderBottom: "1px solid #5A60664D",
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 400, color: "#1A1919" }}
                  >
                    {(params.value || 0).toLocaleString()}
                  </Typography>
                ),
            },
            // ── 5. Collected — NOT editable (progress bar)
            {
              field: "Collected",
              headerName: "Collected ($)",
              flex: 1,
              minWidth: 160,
              filterable: true,
              sortable: true,
              editable: false,
              renderCell: (params) => {
                const progressValue = params.value?.split?.("/")[0]
                  ? Math.min((params.value.split("/")[0] / 10) * 100, 100)
                  : 0;
                if (params.row._isParent) {
                  return (
                    <Chip
                      label={params.value}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: "white",
                        borderBottom: "1px solid #5A60664D",
                      }}
                    />
                  );
                }
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            backgroundColor:
                              progressValue < 50
                                ? "warning.main"
                                : progressValue < 80
                                  ? "info.main"
                                  : "primary.main",
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 400, whiteSpace: "nowrap" }}
                    >
                      {(params.value || 0).toLocaleString()}
                    </Typography>
                  </Box>
                );
              },
            },
          ]
          : []),



        // ── 6. Grade — EDITABLE ✏️
        {
          field: "grade",
          headerName: "Grade",
          width: 100,
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          type: "singleSelect",
          valueOptions: gradeOptions,
          renderHeader: () => (
            <EditableHeader
              label="Grade"
              tooltip="Company performance grade. A+ = Exceptional, A = Excellent, B = Good, C = Average, D/F = Needs Improvement"
            />
          ),
        },

        // ── 7. Revenue — EDITABLE ✏️
        {
          field: "revenue",
          headerName: "Revenue",
          width: 120,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          renderHeader: () => <EditableHeader label="Revenue" />,
        },

        // ── 8. Cash Balance — EDITABLE ✏️
        {
          field: "cash_balance",
          headerName: "Cash Balance",
          width: 130,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly ,
          renderHeader: () => <EditableHeader label="Cash Balance" />,
        },

        // ── 9. Runway — EDITABLE ✏️
        {
          field: "runway",
          headerName: "Runway",
          width: 100,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          renderHeader: () => <EditableHeader label="Runway" />,
        },

        // ── 10. Net Income — EDITABLE ✏️
        {
          field: "net_income",
          headerName: "Net Income",
          width: 120,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          renderHeader: () => <EditableHeader label="Net Income" />,
        },

        // ── 11. Total Headcount — EDITABLE ✏️
        {
          field: "total_head_count",
          headerName: "Total Headcount",
          width: 150,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          type: "number",
          renderHeader: () => <EditableHeader label="Total Headcount" />,
        },

        {
          field: "top_line_kpi",
          headerName: "ARR",
          width: 120,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          renderHeader: () => <EditableHeader label="ARR" />,
        },
        ...customKpiColumns.filter(kpiKey => kpiKey !== 'top_line_kpi').map(kpiKey => ({
          field: kpiKey,
          headerName: CUSTOM_KPI_LABEL_MAP[kpiKey] || kpiKey,
          width: 130,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          renderHeader: () => <EditableHeader label={CUSTOM_KPI_LABEL_MAP[kpiKey] || kpiKey} />,
        })),

        // ── 12. News — EDITABLE ✏️
        {
          field: "news",
          headerName: "News",
          flex: 1,
          minWidth: 200,
          filterable: true,
          sortable: true,
          editable: role !== "operations" && !partnerReadOnly,
          renderHeader: () => <EditableHeader label="News" />,
        },

        // ── 13. Inv. Cap — NOT editable
        /* {
          field: "invested_capital",
          headerName: "Inv. Cap",
          width: 120,
          align: "right",
          headerAlign: "right",
          filterable: true,
          sortable: true,
          editable: false,
        },
 */
        // ── 14. Cash Out Date — NOT editable
        {
          field: "cash_out_date",
          headerName: "Cash Out Date",
          width: 130,
          filterable: true,
          sortable: true,
          editable: false,
        },

        // ── 15. Actions — pinned right
        {
          field: "actions",
          headerName: "Actions",
          width: 160,
          minWidth: 160,
          sortable: false,
          filterable: false,
          editable: false,
          renderCell: (params) => {
            // console.log("Rendering actions for row:", params.row);
            if (params.row._isParent) return null;
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  height: "100%",
                }}
              >
                <CommonButton
                  variant="contained"
                  size="small"
                  color="secondary"
                  sx={{ height: "30px", textTransform: "none" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("CLICK ROW DATA:", params.row); // ✅ ADD THIS

                    console.log("Navigating to review report for:", params.row);
                    navigate(`/grade-reports/${params.row.id}/review-report`, {
                      state: {
                        companyName: params.row.company_name,
                        gradeType: params.row.grade,
                        reportName: reportName,
                        statusType: statusType,

                        // Review page payload
                        partner_doc: params.row.partner_doc_name,
                        partner_name: params.row.partner_name,
                        deal_id: params.row.deal_id ?? 2745, 
                        kpi: params.row.kpi || "CARR",
                        ar_id: params.row.id,
                        requestId: requestId,  // ✅ ADD THIS — the request/report id
                      },
                    });
                  }}
                >
                  Open
                </CommonButton>

                <CommonButton
                  variant="contained"
                  size="medium"
                  color="secondary"
                  sx={flagButtonStyles}
                  aria-label={`Done ${params.row?.Company || ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openHistory(
                      params.row?.company_name || "Unknown Company",
                      params.row?.Grade || "N/A",
                    );
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "0px" }}>
                    <AccessTimeFilledIcon
                      fontSize="small"
                      sx={{ color: "#1A1919" }}
                    />
                  </ListItemIcon>
                </CommonButton>
              </Box>
            );
          },
        },
      ],
      [navigate, role, openHistory, isStatusColumn, statusColors, customKpiColumns],
    );

    const filteredReports = useMemo(() => {
      if (!searchText) return reports;
      const query = searchText.toLowerCase();
      return reports.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(query),
        ),
      );
    }, [reports, searchText]);

    useImperativeHandle(ref, () => ({
      openColumnsPanel: () => apiRef.current?.showPreferences("columns"),
      openFiltersPanel: () => apiRef.current?.showPreferences("filters"),
    }));

    // Render loading state
    if (loading) {
      return (
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#ffffff",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">
            Loading activity records...
          </Typography>
        </Box>
      );
    }

    // Render error state
    if (error) {
      return (
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#ffffff",
          }}
        >
          <Typography variant="body1" color="error">
            Error: {error}
          </Typography>
        </Box>
      );
    }

    // Render empty state
    if (!reports || reports.length === 0) {
      return (
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#ffffff",
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No activity records found
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: "calc(100vh - 200px)",
          width: "100%",
          bgcolor: "#ffffff",
        }}
      >
        <DataGridPro
          ref={tableRef}
          apiRef={apiRef}
          rows={filteredReports}
          columns={columns}
          checkboxSelection={isCheckboxColumn}
          processRowUpdate={handleProcessRowUpdate}
          disableRowSelectionOnClick
          editMode="row"
          loading={loading}
          //isCellEditable={() => role === "partner"}
          initialState={{
            pinnedColumns: { left: ["company_name"], right: ["actions"] },
          }}
          sx={{
            border: "none",

            // ── Column headers
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5 !important",
              borderBottom: "2px solid #e0e0e0",
            },

            // ── All cell borders
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #e0e0e0",
              borderBottom: "1px solid #f0f0f0",
            },

            // ── Editable cells — blue tint (matches PortfolioTable editing mode)
            "& .MuiDataGrid-cell--editable": {
              backgroundColor: "#f0f7ff",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            },
            "& .MuiDataGrid-cell--editable:hover": {
              backgroundColor: "#e3f2fd !important",
            },

            // ── Active editing cell — amber background + blue inset ring
            "& .MuiDataGrid-cell--editing": {
              backgroundColor: "#fff3e0 !important",
              boxShadow: "inset 0 0 0 2px #1976d2",
            },

            // ── Pinned left
            "& .MuiDataGrid-cell--pinnedLeft": {
              backgroundColor: "#fff !important",
              borderRight: "2px solid #d0d0d0 !important",
              boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader--pinnedLeft": {
              backgroundColor: "#f5f5f5 !important",
              borderRight: "2px solid #d0d0d0 !important",
              boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
            },

            // ── Pinned right
            "& .MuiDataGrid-cell--pinnedRight": {
              backgroundColor: "#fff !important",
              borderLeft: "2px solid #d0d0d0 !important",
              boxShadow: "-2px 0 4px rgba(0,0,0,0.05)",
            },
            "& .MuiDataGrid-columnHeader--pinnedRight": {
              backgroundColor: "#f5f5f5 !important",
              borderLeft: "2px solid #d0d0d0 !important",
              boxShadow: "-2px 0 4px rgba(0,0,0,0.05)",
            },

            // ── Scrollbar
            "& ::-webkit-scrollbar": { height: "7px", width: "7px" },
            "& ::-webkit-scrollbar-thumb": {
              background: "#d0d0d0",
              borderRadius: "3px",
              "&:hover": { background: "#b0b0b0" },
            },
          }}
        />
        {/* ── Guided Tour Tooltips ── */}
        {tourStep === 1 && (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "55%",
              transform: "translate(-50%, -50%)",
              zIndex: 1400,
              bgcolor: "#1A1919",
              color: "#fff",
              borderRadius: "12px",
              p: 2.5,
              width: 280,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "-10px",
                top: "50%",
                transform: "translateY(-50%)",
                borderWidth: "10px 10px 10px 0",
                borderStyle: "solid",
                borderColor: "transparent #1A1919 transparent transparent",
              },
            }}
          >
            <Typography fontWeight={700} fontSize={14} mb={0.5}>
              Fill in the data directly in the table.
            </Typography>
            <Typography fontSize={12} color="#ccc" mb={2}>
              Some fields can be edited inline. Click on any blue cell to update its value.
            </Typography>
            <CommonButton
              variant="contained"
              size="small"
              onClick={nextTourStep}
              sx={{ bgcolor: "#1976d2", color: "#fff", borderRadius: "20px", px: 2 }}
            >
              Next
            </CommonButton>
          </Box>
        )}

        {tourStep === 2 && (
          <Box
            sx={{
              position: "fixed",
              top: "30%",
              left: "280px",
              zIndex: 1400,
              bgcolor: "#1A1919",
              color: "#fff",
              borderRadius: "12px",
              p: 2.5,
              width: 280,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "-10px",
                top: "30px",
                borderWidth: "10px 10px 10px 0",
                borderStyle: "solid",
                borderColor: "transparent #1A1919 transparent transparent",
              },
            }}
          >
            <Typography fontWeight={700} fontSize={14} mb={0.5}>
              Change rank
            </Typography>
            <Typography fontSize={12} color="#ccc" mb={2}>
              Hover over a row to reveal the drag handle, then drag rows to change rank.
            </Typography>
            <CommonButton
              variant="contained"
              size="small"
              onClick={nextTourStep}
              sx={{ bgcolor: "#1976d2", color: "#fff", borderRadius: "20px", px: 2 }}
            >
              Next
            </CommonButton>
          </Box>
        )}

        {tourStep === 3 && (
          <Box
            sx={{
              position: "fixed",
              top: "32%",
              right: "150px",
              zIndex: 1400,
              bgcolor: "#1A1919",
              color: "#fff",
              borderRadius: "12px",
              p: 2.5,
              width: 260,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              "&::before": {
                content: '""',
                position: "absolute",
                right: "-10px",
                top: "30px",
                borderWidth: "10px 0 10px 10px",
                borderStyle: "solid",
                borderColor: "transparent transparent transparent #1A1919",
              },
            }}
          >
            <Typography fontWeight={700} fontSize={14} mb={0.5}>
              Open report
            </Typography>
            <Typography fontSize={12} color="#ccc" mb={2}>
              View details and upload documents in the full report.
            </Typography>
            <CommonButton
              variant="contained"
              size="small"
              onClick={nextTourStep}
              sx={{ bgcolor: "#1976d2", color: "#fff", borderRadius: "20px", px: 2 }}
            >
              Got It
            </CommonButton>
          </Box>
        )}

        {/* Backdrop overlay when tour is active */}
        {tourStep && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.3)",
              zIndex: 1300,
            }}
            onClick={nextTourStep}
          />
        )}



        <Box sx={{
          position: "fixed", bottom: 32, right: 32,
          zIndex: 1300, display: "flex", gap: 2
        }}>
          {/* Partner — Submit button shown when changes exist */}
          {role === "partner" && showPartnerSubmit && (
            <CommonButton
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmitRequest}
              sx={{
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                px: 4, py: 1.5, fontWeight: 600,
              }}
            >
              Submit Request
            </CommonButton>
          )}

          {/* Ops — Complete button always shown */}
          {/* {role !== "partner" && statusType === "Submitted" && (
            <CommonButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setCompleteDialogOpen(true)}
              disabled={isCompleted}
              sx={{
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                px: 4, py: 1.5, fontWeight: 600,
                bgcolor: isCompleted ? "#E8F5E9" : "#1E517B",
                color: isCompleted ? "#2E7D32" : "white",
                "&:hover": { bgcolor: isCompleted ? "#E8F5E9" : "#174069" }
              }}
            >
              {isCompleted ? "✓ Completed" : "Complete"}
            </CommonButton>
          )} */}
        </Box>
        {/* Complete Report Dialog */}
        <Dialog
          open={completeDialogOpen}
          onClose={() => setCompleteDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: "16px", p: 1, width: "480px", maxWidth: "95vw" } }}
        >
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>Complete Report</Typography>
          </Box>
          <DialogContent sx={{ px: 3, pt: 1 }}>
            <Typography sx={{ fontSize: "13px", color: "#6B7280", mb: 2, lineHeight: 1.6 }}>
              Add a comment to supplement information for future reports or regarding any changes in reporting.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Enter comment..."
              value={completeComment}
              onChange={(e) => setCompleteComment(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => setCompleteDialogOpen(false)}
              sx={{ textTransform: "none", color: "#6B7280", fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompleteRequest}
              variant="contained"
              disabled={completing}
              sx={{
                textTransform: "none",
                bgcolor: "#1E517B",
                borderRadius: "8px",
                fontWeight: 600,
                px: 3,
                "&:hover": { bgcolor: "#174069" }
              }}
            >
              {completing ? "Completing..." : "Complete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={isCompleted}
          autoHideDuration={4000}
          onClose={() => { }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity="success" icon={false} sx={{ borderRadius: "12px", boxShadow: 3 }}>
            <Typography fontWeight={600} fontSize={14}>Request completed</Typography>
            <Typography fontSize={12} color="#6B7280">
              {reportName || "Activity Report"} is now completed.
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    );
  },
);

export default PartnerReports;